/**
 * Customer Review Form System
 * Handles review submission with purchase verification and validation
 */
class ReviewForm {
  constructor(options = {}) {
    this.apiUrl = options.apiUrl || window.reviewSystem?.apiUrl || '';
    this.productId = options.productId || window.reviewSystem?.productId || '';
    this.productTitle = options.productTitle || window.reviewSystem?.productTitle || '';
    this.container = document.getElementById(options.containerId || 'review-form-container');
    
    // Form state
    this.isSubmitting = false;
    this.isVerified = false;
    this.customerEmail = '';
    this.formData = {
      rating: 0,
      title: '',
      content: '',
      customerName: '',
      customerEmail: ''
    };

    // Initialize if container exists
    if (this.container && this.productId) {
      this.init();
    }
  }

  init() {
    this.renderForm();
    this.setupEventListeners();
  }

  renderForm() {
    if (!this.container) return;

    this.container.innerHTML = `
      <div class="review-form-wrapper">
        <div class="review-form-header">
          <h3>Write a Review</h3>
          <p>Share your experience with ${this.escapeHtml(this.productTitle)}</p>
        </div>

        <!-- Purchase Verification Step -->
        <div id="verification-step" class="form-step ${this.isVerified ? 'form-step--completed' : 'form-step--active'}">
          <div class="form-step__header">
            <span class="form-step__number">1</span>
            <h4>Verify Your Purchase</h4>
          </div>
          
          <div class="form-step__content">
            ${this.renderVerificationForm()}
          </div>
        </div>

        <!-- Review Form Step -->
        <div id="review-step" class="form-step ${this.isVerified ? 'form-step--active' : 'form-step--disabled'}">
          <div class="form-step__header">
            <span class="form-step__number">2</span>
            <h4>Share Your Review</h4>
          </div>
          
          <div class="form-step__content">
            ${this.renderReviewForm()}
          </div>
        </div>

        <!-- Success/Error Messages -->
        <div id="form-messages" class="form-messages"></div>
      </div>
    `;
  }

  renderVerificationForm() {
    if (this.isVerified) {
      return `
        <div class="verification-success">
          <span class="verification-success__icon">✓</span>
          <span class="verification-success__text">Purchase verified for ${this.escapeHtml(this.customerEmail)}</span>
        </div>
      `;
    }

    return `
      <div class="verification-form">
        <p class="verification-form__description">
          To ensure authentic reviews, please verify your purchase by entering the email address used for your order.
        </p>
        
        <div class="form-group">
          <label for="verification-email" class="form-label">Order Email Address</label>
          <input 
            type="email" 
            id="verification-email" 
            class="form-input"
            placeholder="Enter your order email"
            required
            autocomplete="email"
          />
          <span class="form-help">We'll check that you purchased this product</span>
        </div>

        <button 
          type="button" 
          id="verify-purchase-btn" 
          class="btn btn--primary"
        >
          <span class="btn__text">Verify Purchase</span>
          <span class="btn__spinner" style="display: none;">
            <div class="spinner"></div>
          </span>
        </button>
      </div>
    `;
  }

  renderReviewForm() {
    return `
      <form id="review-form" class="review-form ${!this.isVerified ? 'review-form--disabled' : ''}">
        <!-- Star Rating -->
        <div class="form-group">
          <label class="form-label">Overall Rating <span class="form-required">*</span></label>
          <div class="star-rating-input" id="star-rating-input">
            ${[1, 2, 3, 4, 5].map(rating => `
              <button 
                type="button" 
                class="star-rating-input__star" 
                data-rating="${rating}"
                aria-label="${rating} star${rating !== 1 ? 's' : ''}"
                ${!this.isVerified ? 'disabled' : ''}
              >
                ★
              </button>
            `).join('')}
          </div>
          <div class="rating-text" id="rating-text">Select a rating</div>
        </div>

        <!-- Customer Name -->
        <div class="form-group">
          <label for="customer-name" class="form-label">Your Name <span class="form-required">*</span></label>
          <input 
            type="text" 
            id="customer-name" 
            class="form-input"
            placeholder="Enter your name"
            required
            maxlength="100"
            ${!this.isVerified ? 'disabled' : ''}
          />
        </div>

        <!-- Review Title -->
        <div class="form-group">
          <label for="review-title" class="form-label">Review Title</label>
          <input 
            type="text" 
            id="review-title" 
            class="form-input"
            placeholder="Summarize your experience"
            maxlength="150"
            ${!this.isVerified ? 'disabled' : ''}
          />
        </div>

        <!-- Review Content -->
        <div class="form-group">
          <label for="review-content" class="form-label">Your Review <span class="form-required">*</span></label>
          <textarea 
            id="review-content" 
            class="form-textarea"
            placeholder="Tell others about your experience with this product. What did you like or dislike?"
            required
            minlength="10"
            maxlength="2000"
            rows="5"
            ${!this.isVerified ? 'disabled' : ''}
          ></textarea>
          <div class="character-count">
            <span id="content-count">0</span>/2000 characters
          </div>
        </div>

        <!-- Terms and Submit -->
        <div class="form-group">
          <label class="checkbox-label">
            <input 
              type="checkbox" 
              id="terms-agreement" 
              class="checkbox-input"
              required
              ${!this.isVerified ? 'disabled' : ''}
            />
            <span class="checkbox-custom"></span>
            <span class="checkbox-text">
              I agree that this review represents my honest opinion and experience with this product.
            </span>
          </label>
        </div>

        <button 
          type="submit" 
          id="submit-review-btn" 
          class="btn btn--success btn--large"
          ${!this.isVerified ? 'disabled' : ''}
        >
          <span class="btn__text">Submit Review</span>
          <span class="btn__spinner" style="display: none;">
            <div class="spinner"></div>
          </span>
        </button>
      </form>
    `;
  }

  setupEventListeners() {
    // Purchase verification
    const verifyBtn = document.getElementById('verify-purchase-btn');
    if (verifyBtn) {
      verifyBtn.addEventListener('click', () => this.handleVerification());
    }

    // Email validation on input
    const emailInput = document.getElementById('verification-email');
    if (emailInput) {
      emailInput.addEventListener('input', () => this.clearMessages());
      emailInput.addEventListener('keypress', (e) => {
        if (e.key === 'Enter') this.handleVerification();
      });
    }

    // Star rating interaction
    const starContainer = document.getElementById('star-rating-input');
    if (starContainer) {
      this.setupStarRating(starContainer);
    }

    // Character counter
    const contentTextarea = document.getElementById('review-content');
    if (contentTextarea) {
      contentTextarea.addEventListener('input', () => this.updateCharacterCount());
    }

    // Form submission
    const reviewForm = document.getElementById('review-form');
    if (reviewForm) {
      reviewForm.addEventListener('submit', (e) => this.handleSubmission(e));
    }

    // Real-time validation
    this.setupFormValidation();
  }

  setupStarRating(container) {
    const stars = container.querySelectorAll('.star-rating-input__star');
    const ratingText = document.getElementById('rating-text');

    stars.forEach((star, index) => {
      // Hover effect
      star.addEventListener('mouseenter', () => {
        if (this.isVerified) {
          this.highlightStars(stars, index + 1);
          ratingText.textContent = this.getRatingText(index + 1);
        }
      });

      // Click selection
      star.addEventListener('click', () => {
        if (this.isVerified) {
          this.formData.rating = index + 1;
          this.selectStars(stars, index + 1);
          ratingText.textContent = this.getRatingText(index + 1);
          this.clearFieldError('star-rating-input');
        }
      });
    });

    // Reset on mouse leave
    container.addEventListener('mouseleave', () => {
      if (this.isVerified) {
        this.selectStars(stars, this.formData.rating);
        ratingText.textContent = this.formData.rating > 0 
          ? this.getRatingText(this.formData.rating)
          : 'Select a rating';
      }
    });
  }

  highlightStars(stars, rating) {
    stars.forEach((star, index) => {
      star.classList.toggle('star-rating-input__star--hover', index < rating);
    });
  }

  selectStars(stars, rating) {
    stars.forEach((star, index) => {
      star.classList.toggle('star-rating-input__star--selected', index < rating);
    });
  }

  getRatingText(rating) {
    const texts = {
      1: 'Poor',
      2: 'Fair', 
      3: 'Good',
      4: 'Very Good',
      5: 'Excellent'
    };
    return texts[rating] || 'Select a rating';
  }

  setupFormValidation() {
    const inputs = ['customer-name', 'review-content'];
    
    inputs.forEach(inputId => {
      const input = document.getElementById(inputId);
      if (input) {
        input.addEventListener('blur', () => this.validateField(inputId));
        input.addEventListener('input', () => this.clearFieldError(inputId));
      }
    });
  }

  validateField(fieldId) {
    const field = document.getElementById(fieldId);
    if (!field) return true;

    let isValid = true;
    let errorMessage = '';

    switch (fieldId) {
      case 'customer-name':
        if (!field.value.trim()) {
          isValid = false;
          errorMessage = 'Please enter your name';
        } else if (field.value.trim().length < 2) {
          isValid = false;
          errorMessage = 'Name must be at least 2 characters';
        }
        break;

      case 'review-content':
        if (!field.value.trim()) {
          isValid = false;
          errorMessage = 'Please write your review';
        } else if (field.value.trim().length < 10) {
          isValid = false;
          errorMessage = 'Review must be at least 10 characters';
        }
        break;
    }

    if (!isValid) {
      this.showFieldError(fieldId, errorMessage);
    }

    return isValid;
  }

  showFieldError(fieldId, message) {
    const field = document.getElementById(fieldId);
    const group = field?.closest('.form-group');
    
    if (group) {
      group.classList.add('form-group--error');
      
      // Remove existing error
      const existingError = group.querySelector('.form-error');
      if (existingError) existingError.remove();
      
      // Add new error
      const errorEl = document.createElement('span');
      errorEl.className = 'form-error';
      errorEl.textContent = message;
      group.appendChild(errorEl);
    }
  }

  clearFieldError(fieldId) {
    const field = document.getElementById(fieldId);
    const group = field?.closest('.form-group');
    
    if (group) {
      group.classList.remove('form-group--error');
      const errorEl = group.querySelector('.form-error');
      if (errorEl) errorEl.remove();
    }
  }

  updateCharacterCount() {
    const textarea = document.getElementById('review-content');
    const counter = document.getElementById('content-count');
    
    if (textarea && counter) {
      const count = textarea.value.length;
      counter.textContent = count;
      
      // Update styling based on character count
      const group = textarea.closest('.form-group');
      if (group) {
        group.classList.toggle('form-group--warning', count > 1800);
        group.classList.toggle('form-group--danger', count > 1950);
      }
    }
  }

  async handleVerification() {
    const emailInput = document.getElementById('verification-email');
    const verifyBtn = document.getElementById('verify-purchase-btn');
    
    if (!emailInput || !verifyBtn) return;

    const email = emailInput.value.trim();
    
    // Client-side validation
    if (!email) {
      this.showMessage('Please enter your email address', 'error');
      emailInput.focus();
      return;
    }

    if (!this.isValidEmail(email)) {
      this.showMessage('Please enter a valid email address', 'error');
      emailInput.focus();
      return;
    }

    try {
      this.setButtonLoading(verifyBtn, true);

      this.clearMessages();
      // Move productId and email to query string
      const verifyUrl = `${this.apiUrl}/api/v1/reviews/verify-purchase?email=${encodeURIComponent(email)}&productId=${encodeURIComponent(this.productId)}`;
      const response = await fetch(verifyUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: null
      });

      const data = await response.json();

      // Accept both 'verified' and 'canReview' as success
      if (response.ok && (data.verified === true || data.canReview === true)) {
        this.isVerified = true;
        this.customerEmail = email;
        this.formData.customerEmail = email;
        this.showMessage('Purchase verified successfully! You can now write your review.', 'success');
        this.updateFormAfterVerification();
      } else {
        this.showMessage(
          data.message || 'Purchase verification failed. Please check your email address or contact support.',
          'error'
        );
      }

    } catch (error) {
      console.error('Verification error:', error);
      this.showMessage(
        'Unable to verify purchase. Please check your internet connection and try again.',
        'error'
      );
    } finally {
      this.setButtonLoading(verifyBtn, false);
    }
  }

  updateFormAfterVerification() {
    // Update verification step
    const verificationStep = document.getElementById('verification-step');
    const reviewStep = document.getElementById('review-step');
    
    if (verificationStep && reviewStep) {
      verificationStep.classList.remove('form-step--active');
      verificationStep.classList.add('form-step--completed');
      reviewStep.classList.remove('form-step--disabled');
      reviewStep.classList.add('form-step--active');
    }

    // Re-render the steps with new state
    this.renderForm();
    this.setupEventListeners();

    // Focus on the first form field
    setTimeout(() => {
      const firstInput = document.getElementById('customer-name');
      if (firstInput) firstInput.focus();
    }, 100);
  }

  async handleSubmission(event) {
    event.preventDefault();
    
    if (!this.isVerified) {
      this.showMessage('Please verify your purchase first', 'error');
      return;
    }

    if (this.isSubmitting) return;

    // Collect form data
    this.collectFormData();

    // Validate form
    if (!this.validateForm()) {
      return;
    }

    const submitBtn = document.getElementById('submit-review-btn');

    try {
      this.isSubmitting = true;
      this.setButtonLoading(submitBtn, true);
      this.clearMessages();

      const response = await fetch(`${this.apiUrl}/api/v1/reviews/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          productId: this.productId,
          customerEmail: this.formData.customerEmail,
          customerName: this.formData.customerName,
          rating: this.formData.rating,
          title: this.formData.title,
          content: this.formData.content
        })
      });

      const data = await response.json();

      if (response.ok) {
        this.showMessage(
          'Thank you for your review! It has been submitted and will be published after moderation.',
          'success'
        );
        
        this.resetForm();
        
        // Refresh review display if available
        if (window.reviewDisplay) {
          setTimeout(() => {
            window.reviewDisplay.loadReviewStats();
            window.reviewDisplay.loadReviews();
            window.reviewDisplay.renderSummary();
            window.reviewDisplay.renderReviews();
          }, 1000);
        }

      } else {
        this.showMessage(
          data.message || 'Failed to submit review. Please try again.',
          'error'
        );
      }

    } catch (error) {
      console.error('Submission error:', error);
      this.showMessage(
        'Unable to submit review. Please check your internet connection and try again.',
        'error'
      );
    } finally {
      this.isSubmitting = false;
      this.setButtonLoading(submitBtn, false);
    }
  }

  collectFormData() {
    this.formData.customerName = document.getElementById('customer-name')?.value.trim() || '';
    this.formData.title = document.getElementById('review-title')?.value.trim() || '';
    this.formData.content = document.getElementById('review-content')?.value.trim() || '';
  }

  validateForm() {
    let isValid = true;

    // Validate rating
    if (this.formData.rating === 0) {
      this.showFieldError('star-rating-input', 'Please select a rating');
      isValid = false;
    }

    // Validate required fields
    if (!this.validateField('customer-name')) isValid = false;
    if (!this.validateField('review-content')) isValid = false;

    // Validate terms agreement
    const termsCheckbox = document.getElementById('terms-agreement');
    if (termsCheckbox && !termsCheckbox.checked) {
      this.showMessage('Please agree to the terms to submit your review', 'error');
      isValid = false;
    }

    return isValid;
  }

  resetForm() {
    this.formData = {
      rating: 0,
      title: '',
      content: '',
      customerName: '',
      customerEmail: this.customerEmail
    };

    const form = document.getElementById('review-form');
    if (form) form.reset();

    // Reset star rating
    const stars = document.querySelectorAll('.star-rating-input__star');
    stars.forEach(star => {
      star.classList.remove('star-rating-input__star--selected');
    });

    const ratingText = document.getElementById('rating-text');
    if (ratingText) ratingText.textContent = 'Select a rating';

    // Reset character count
    this.updateCharacterCount();

    // Clear all errors
    document.querySelectorAll('.form-group--error').forEach(group => {
      group.classList.remove('form-group--error');
    });
    document.querySelectorAll('.form-error').forEach(error => {
      error.remove();
    });
  }

  showMessage(message, type = 'info') {
    const messagesContainer = document.getElementById('form-messages');
    if (!messagesContainer) return;

    messagesContainer.innerHTML = `
      <div class="form-message form-message--${type}">
        <div class="form-message__icon">
          ${type === 'success' ? '✓' : type === 'error' ? '✕' : 'ℹ'}
        </div>
        <div class="form-message__text">${message}</div>
      </div>
    `;

    // Auto-hide success messages
    if (type === 'success') {
      setTimeout(() => this.clearMessages(), 5000);
    }

    // Scroll to message
    messagesContainer.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  clearMessages() {
    const messagesContainer = document.getElementById('form-messages');
    if (messagesContainer) {
      messagesContainer.innerHTML = '';
    }
  }

  setButtonLoading(button, loading) {
    if (!button) return;

    const text = button.querySelector('.btn__text');
    const spinner = button.querySelector('.btn__spinner');

    if (loading) {
      button.disabled = true;
      button.classList.add('btn--loading');
      if (text) text.style.display = 'none';
      if (spinner) spinner.style.display = 'inline-block';
    } else {
      button.disabled = false;
      button.classList.remove('btn--loading');
      if (text) text.style.display = 'inline';
      if (spinner) spinner.style.display = 'none';
    }
  }

  isValidEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}

// Auto-initialize when DOM is ready
document.addEventListener('DOMContentLoaded', function() {
  // Check if we're on a product page with review form container
  if (document.getElementById('review-form-container') && window.reviewSystem) {
    window.reviewForm = new ReviewForm();
  }
});

// Export for manual initialization
window.ReviewForm = ReviewForm;